import { createRef, useEffect, useContext } from "react";
import EntryTimeForm from "../components/EntryTimeForm";
import EntryTimeTable from "../components/EntryTimeTable";
import getConfig from "../config/tableConfig";
import JSONSchemaForm from "@rjsf/core";
import getFiltersConfig from "../config/filterConfig";
import { Card } from "react-bootstrap";
import getFilterUIConfig from "../config/filterUIConfig";
import moment from "moment";
import { EntryTimeContext } from "../contexts/EntryTimeContext";
import useIndex from "../services/useIndex";
import useInitialize from "../services/useInitialize";
import useGet from "../services/useGet";
import usePrepareRemove from "../services/usePrepareRemove";
import useRemove from "../services/useRemove";
import useStore from "../services/useStore";
import useUpdate from "../services/useUpdate";
import usePrepareAddTime from "../services/usePrepareAddTime";
import EntryTimeAddTimeForm from "../components/EntryTimeAddTimeForm";
import useAddTime from "../services/useAddTime";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import AddQueryParamsToURL from "@beeldit/core/helpers/AddQueryParamsToURL";
import useFilters from "@beeldit/core/helpers/useFilters";
import CrudModuleFactoryTableHeaderActions from "@beeldit/core/components/CrudModuleFactoryTableHeaderActions";
import CrudModuleFactoryTableFilters from "@beeldit/core/components/CrudModuleFactoryTableFilters";
import CrudModuleFactoryTablePagination from "@beeldit/core/components/CrudModuleFactoryTablePagination";
import CrudModuleFactoryModal from "@beeldit/core/components/CrudModuleFactoryModal";
import CrudModuleFactoryConfirmationModal from "@beeldit/core/components/CrudModuleFactoryConfirmationModal";
import AddTimeModal from "../components/AddTimeModal";

function EntryTimesPage() {
  const { user } = useContext(AuthContext);

  const addQueryParamsToURL: any = AddQueryParamsToURL();

  /** Crud functions */
  const index = useIndex();
  const indexFunction = () => index();

  const get = useGet();
  const getFunction = (id: any) => get(id);

  const initialize = useInitialize();
  const initializeFunction = () => initialize();

  /**
   * Form store success function definition
   */
  const onSuccessStore = () => {
    handleClose(); // We pass the function to close the modal on success
    index(); // We pass the function to refresh the table on success
  };

  const store = useStore();
  const storeFunction = () => store(onSuccessStore);

  /**
   * Form update success function definition
   */
  const onSuccessUpdate = () => {
    handleClose(); // We pass the function to close the modal on success
    index(); // We pass the function to refresh the table on success
  };

  const update = useUpdate();
  const updateFunction = () => update(onSuccessUpdate);

  const prepareRemove = usePrepareRemove();
  const prepareRemoveFunction = (id: any) => prepareRemove(id);

  const remove = useRemove();
  const onSuccessRemove = () => {
    indexFunction();
  };
  const removeFunction = () => remove(onSuccessRemove);

  const copy = (element: any) => {
    element.id = null;
    element.date = moment().format("YYYY-MM-DD");
    element.minutes = 0;
    return element;
  };

  const copyFunction = (id: any) => get(id, copy);

  const prepareAddTime = usePrepareAddTime();
  const prepareAddTimeFunction = (id: any) => prepareAddTime(id);

  const addTime = useAddTime();
  const onSuccessAddTime = () => {
    indexFunction();
  };
  const addTimeFunction = (minutes: number) =>
    addTime(minutes, onSuccessAddTime);

  /** Filter loading */
  const { filtersLoaded } = useContext(EntryTimeContext);
  const { tableFilters, setTableFilters } = useContext(EntryTimeContext);
  useFilters(
    { customer_id: null, status_id: null, report_id: null },
    EntryTimeContext
  );

  /**Order by config */
  const { orderBy, setOrderBy } = useContext(EntryTimeContext);

  const onOrderByChange = (orderBy: string) => {
    setOrderBy(orderBy);
  };

  /**
   * Pagination config
   */
  const { paginationConfig, setPaginationConfig } =
    useContext(EntryTimeContext);

  const onPaginationChange = (page: number) => {
    setPaginationConfig({ ...paginationConfig, page: page });
  };

  /** Index table funcion definition */
  const { tableData } = useContext(EntryTimeContext);

  useEffect(() => {
    if (filtersLoaded) {
      index();
    }
  }, [tableFilters, paginationConfig, filtersLoaded, orderBy]);

  /** Creation and edition modal function and state definition */
  const { showModal, setShowModal } = useContext(EntryTimeContext);
  const handleClose = () => setShowModal(false); // We pass the function to close the modal on success

  /** Creation and edition modal element definition */
  const { element, setElement } = useContext(EntryTimeContext);

  /**
   * Form Reference definition
   */
  const formRef = createRef<JSONSchemaForm>();

  /**
   * Creation and edition modal configuration definition
   */
  const modalConfig = {
    context: EntryTimeContext,
    title: element && element.id ? "edit entry time" : "create entry time",
    onSubmit: () => formRef.current?.submit(), //We pass the function for the submit of the modal
  };

  /**
   * Delete Confirmation modal configuration definition
   */
  const deleteConfirmationModalConfig = {
    context: EntryTimeContext,
    onSubmit: () => removeFunction(), //We pass the function for the submit of the modal
  };

  /**
   * Form configuration definition
   */
  const formConfig = {
    formRef: formRef, // We pass the ref to the form
    onSubmit: (form: any) => {
      if (form.formData.id) {
        // If the element has an id, we update it
        updateFunction();
      } else {
        // else we create it
        storeFunction();
      }
    },
    element: element, // We pass the element to the form in order to render it in the modal form
  };

  /**
   * Table configuration definition
   */
  const tableConfig = getConfig({
    initializeFunction, // We pass the function to initialize a new element (used in the create button)
    indexFunction, // We pass the function to index the table
    getFunction, // We pass the function to get an element (used in the edit button)
    prepareRemoveFunction,
    copyFunction,
    prepareAddTimeFunction,
  });

  /** Table Filters configuration */
  const filterUIConfig = getFilterUIConfig(user);
  const filterConfig = {
    schema: getFiltersConfig(user),
    uiSchema: filterUIConfig.uiScheme,
    widgets: filterUIConfig.widgets,
    element: tableFilters,
    onChange: (form: any) => {
      setTableFilters(form.formData);
      addQueryParamsToURL(form.formData);
    },
  };

  const { showAddTimeFormModal, setShowAddTimeFormModal } =
    useContext(EntryTimeContext);
  /**
   * Add time modal and form configuration definition
   */
  const addTimeFormRef = createRef<JSONSchemaForm>();
  const addTimeFormConfig = {
    formRef: addTimeFormRef, // We pass the ref to the form
    onSubmit: (form: any) => {
      addTimeFunction(form.formData.minutes);
    },
    element: {}, // We pass the element to the form in order to render it in the modal form
  };

  const addTimeModalConfig = {
    context: EntryTimeContext,
    title: "add time",
    onSubmit: () => addTimeFormRef.current?.submit(),
    customShowModal: showAddTimeFormModal,
    customSetShowModal: (show: boolean) => setShowAddTimeFormModal(show),
  };


  return (
    <Card className="p-3">
      <Card.Title className="d-flex align-items-center justify-content-between ps-3">
        <span>Horas de entrada</span>
        <div>
          <CrudModuleFactoryTableHeaderActions
            actions={tableConfig.actions}
          ></CrudModuleFactoryTableHeaderActions>
        </div>
      </Card.Title>
      <Card.Body>
        {filtersLoaded ? (
          <>
            <CrudModuleFactoryTableFilters formConfig={filterConfig} />
            <EntryTimeTable
              tableData={tableData}
              tableConfig={tableConfig}
              onOrderByChange={onOrderByChange}
            />
            <CrudModuleFactoryTablePagination
              paginationConfig={paginationConfig}
              totalElements={tableData.totalElements}
              onPaginationChange={onPaginationChange}
            ></CrudModuleFactoryTablePagination>
          </>
        ) : null}
        <CrudModuleFactoryModal modalConfig={modalConfig}>
          <EntryTimeForm formConfig={formConfig} />
        </CrudModuleFactoryModal>
        <AddTimeModal modalConfig={addTimeModalConfig}>
          <EntryTimeAddTimeForm formConfig={addTimeFormConfig}/>
        </AddTimeModal>
        <CrudModuleFactoryConfirmationModal
          modalConfig={deleteConfirmationModalConfig}
        >
          ¿Desear eliminar el elemento?
        </CrudModuleFactoryConfirmationModal>
      </Card.Body>
    </Card>
  );
}

export default EntryTimesPage;
